import React, {
  useEffect,
  useRef,
  useState
} from "react";
import {
  FormProvider,
  useForm,
} from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  colors,
  gaps
} from "theme/defaultStyle";
import { Document } from "entities/UIModel/Document";
import { Document as ApiDocument} from "entities/ApiModel/Document";
import { Order } from "utils/sorting";
import StewartTable from "controls/global/stewart-table";
import {
  Box,
  IconButton,
  styled
} from "@mui/material";
import { 
  SelectionConfig, 
  StewartTableColumn 
} from "controls/global/stewart-table/StewartTable";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import CustomTooltip from "controls/global/custom-tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import ViewIcon from "theme/icons/ViewIcon";
import DownloadIcon from "theme/icons/DownloadIcon";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";
import ErrorDialog from "controls/global/dialogs/error-dialog";
import PdfViewer from "controls/global/pdf-viewer";
import CustomTextField from "controls/global/text-input-field/CustomTextField";
import {
  ActionResult,
  PdfDocument,
} from "entities/UIModel";
import { useDocument } from "utils/context/DocumentContext";
import {
  ActionResultStatus,
  DownloadFileType,
  FileSizeType,
  PERMISSIONS,
  ProductType,
  TitleSearchProcessedStatusType,
  UIConstants
} from "utils/data/enum";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import ConfirmationDialogV2 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV2";
import { NavigateToInfo } from "entities/UIModel/NavigationInfo";
import { getNavigationInfo } from "utils/services/utilsService";
import useNavRedirect from "utils/custom-hooks/useNavRedirect";
import { 
  getUrlByMenuId, 
  menuIds 
} from "pages/home/menu";
import { useFiles } from "utils/context/FilesContext";
import { getPdfDocument } from "pages/file/utils/helper";
import { 
  downloadByBase64, 
  formatDateRegEx, 
  formatFileSize, 
  getBase64DataType 
} from "utils/shared";
import { 
  useTitleSearchActions, 
  useTitleSearchGridStates 
} from "utils/context/TitleSearchContext";
import { showToast } from "controls/global/stewart-toast/showToast";
import ToggleOnIcon from "theme/icons/ToggleOnIcon";
import ToggleOffIcon from "theme/icons/ToggleOffIcon";
// import { useHasSentMessageSuccessfully } from "utils/context/TitleMessageContext";

const StyledTableContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  "& .MuiTableContainer-root": {
    minHeight: "80px",
    maxHeight: "292px",
  },
  "& thead": {
    "& tr th#documentDisplayFileName": {
      minWidth: "40% !important",
    },
    "& tr th#documentUploadedDate": {
      width: "200px !important",
      // textWrap: "nowrap",
      whiteSpace: "nowrap",
    },
    "& tr th#documentUploadedByUserName": {
      width: "140px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#Actions": {
      // width: "150px !important",
      whiteSpace: "nowrap",
    },
  },
  "& tr td.actions": {
    whiteSpace: "nowrap",
  },
  "& tbody": {
    "& tr:hover td": {
      background: colors.grey10,
    },
    "& td .actionButton": {
      width: "40px",
    }
  },
  "&.editing-row": {
    marginLeft: "30px !important",
  },
  "& tbody .MuiTableRow-root": {
    "&.editing-row": {
      "& td": {
        ":last-child": {
          "& div.MuiBox-root": {
            "marginLeft": "40px !important",
          }
        },
      },
    },
    "&.resware": {
      backgroundColor: `${colors.lightBlue} !important`,
    },
  },
  "& tbody tr.hiddenRow .MuiTableCell-root": {
    color: "#7F7F7F",
  },  
  // override row-header-cell for checbox
  "& .MuiTableCell-root": {
    "&.row-header-cell": {
      backgroundColor: "unset",
    }
  },

});

const editGridSchema = yup.object().shape({
  documentDisplayFileName: yup
    .string()
    .trim()
    .nullable()
    .required("Required Field")
});

const MAIN_MESSAGE = "Save changes?";
const ADDITIONAL_MESSAGE = 'Click "Save" to save your changes. Click "Continue" to navigate away without saving.';
const ADDITIONAL_MESSAGE2 = 'Click "Save & Exit" to save your changes and exit. Click "Continue" to exit without saving.';
const DELETE_SUCCESS_TITLE = "Document deleted successfully!";
const SAVE_DOCUMENT_NAME_SUCCESS_TITLE = "Document name edited successfully";
const UPDATE_DOCUMENT_ISHIDDEN_SUCCESS_TITLE = "Document successfully hidden!";
const UPDATE_DOCUMENT_ISHIDDEN_SUCCESS_MESSAGE = "Hiding documents will prevent them from appearing to external users, but they can be unhidden by using the unhide button.";

interface Props {
  disabled: boolean;
  selectionConfig?: SelectionConfig<Document>;
  processedStatusTypeCode?: string | null;
  page: number;
  rows: Document[];
  orderBy?: keyof Document;
  order: Order;
  noRowsMessage: string[];
  onRequestSort?: (property: any, disableToggling?: boolean) => void;
  performSearch?: () => Promise<void>;
  saveFile: () => Promise<void>;
  reloadFile: () => Promise<void>;
  reloadFileActivities: () => Promise<void>;
}

const UploadedDocumentsGrid: React.FC<Props> = ({
  disabled = false,
  selectionConfig,
  processedStatusTypeCode,
  page,
  rows,
  orderBy,
  order,
  noRowsMessage,
  onRequestSort,
  performSearch,
  saveFile,
  reloadFile,
  reloadFileActivities,
}) => {

  // React-hook-form 
  const methods = useForm({
    resolver: yupResolver(editGridSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { setValue, getValues, resetField } = methods;

  // Is Read Only file
  const [{ isReadOnly }]  = useFiles();

  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasHideUnHideDocsAccess = permissionCodes?.includes(PERMISSIONS.TITLESEARCH_HIDEUNHIDEDOCS);

  // Document context
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();
  const [{ isTitleSearchGridInEditMode}] =  useTitleSearchGridStates();
  const [, { setTitleSearchGridInEditMode }] = useTitleSearchActions();
  const [, { saveDocument, getDocumentUrl, deleteDocument, getDocument}] = useDocument();
  
  const [disableSave, setDisableSave] = useState<boolean>(true);
  const [currentEditingRow, setCurrentEditingRow] = useState<Document>();
  const [openSaveImageDialog, setOpenSaveImageDialog] = useState<boolean>(false);
  const [openSaveExitImageDialog, setOpenSaveExitImageDialog] = useState<boolean>(false);
  const [openSaveErrorDialog, setOpenSaveErrorDialog] = useState<boolean>(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>("");
  const { navigateAfterSaveChangesDialog } = useNavRedirect()
  const [isProductStatusVisible, setIsProductStatusVisible] = useState<boolean>(false);
  // const [{ hasSentMessageSuccessfully }] = useHasSentMessageSuccessfully();

  const navigateTo = useRef<NavigateToInfo>({});
  
  const handleEdit = (rowData: Document) => {
    setTitleSearchGridInEditMode(true);
    rowData.isEdit = true;
    resetField(`documentDisplayFileName`);
    setCurrentEditingRow(rowData);
  };

  // Delete document 
  const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);

  // View Confirmation Message
  const [deleteConfirmationMessage, setDeleteConfirmationMessage] = useState("")
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  // Error dialog
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>("");
  const [correlationId, setCorrelationId] = React.useState<string>("");
  const getCorrelationId = (httpResponseHeaders: any): string => {
    if (httpResponseHeaders && httpResponseHeaders["x-correlation-id"]) {
      return httpResponseHeaders["x-correlation-id"];
    }
    return "";
  };

  // View PDF, edit document name, delete document
  const [openPdfViewer, setOpenPdfViewer] = useState(false);
  const [pdfDocuments, setPdfDocuments] = useState<PdfDocument[]>([]);

  const mapRequiredApiDocument = (document: Document) => {
      const apiDocument: ApiDocument = {
        DocumentID: document.documentID,
        FileID: document.fileID,
        OrderID: document.orderID,
        IntegrationKey: document.integrationKey,
        DocumentCreatedByUserID: document.documentCreatedByUserID,
        DocumentCreatedDate: document.documentCreatedDateOriginal,
        DocumentPath: document.documentPath,
        DocumentFileName: document.documentFileName,
        DocumentStatusTypeCode: document.documentStatusTypeCode,
      }

      return apiDocument;
  }

  const handleView = async (data: Document) => {
    if (
      data.documentFileTypeCode &&
      data.documentFileTypeCode.toLowerCase() === DownloadFileType.pdf
    ) {
      const actionResult: ActionResult = await getDocumentUrl(
        data.fileID,
        ProductType.TitleSearch.toString(),
        data.orderID,
        data.rowId,
        1
      );

      if (actionResult && (actionResult.error || !actionResult.documentInfo)) {
        setErrorMessage(UIConstants.VIEW_DOCUMENT_ERROR_MESSAGE);
        setCorrelationId(
          getCorrelationId(actionResult.error.response?.headers)
        );
        setOpenErrorDialog(true);
        return;
      }

      if (
        actionResult &&
        actionResult.documentInfo &&
        actionResult.documentId
      ) {
        setPdfDocuments([
          getPdfDocument(
            undefined,
            undefined,
            undefined,
            actionResult.documentId || 0,
            actionResult.documentInfo,
            data.documentDisplayFileName || "document",
            0
          ),
        ]);
        setOpenPdfViewer(true);
      }
    } else {
      handleDownload(data);
    }
  };

  const handleDownload = async(data: Document) => {
    const actionResult: ActionResult = await getDocument(
      data.rowId,
      data.documentDisplayFileName,
      1
    );

    if (actionResult && actionResult.error) {
      // showToast({
      //   type: "error",
      //   title: UIConstants.DOWNLOAD_DOCUMENT_ERROR_MESSAGE.toString(),
      // });
      setErrorMessage(UIConstants.DOWNLOAD_DOCUMENT_ERROR_MESSAGE);
      setCorrelationId(
        getCorrelationId(actionResult.error.response?.headers)
      );
      setOpenErrorDialog(true);
      return;
    }

    downloadByBase64(
      `data:${getBase64DataType(
        data.documentFileTypeCode!.toLowerCase()
      )};base64,${actionResult.apiResponse}`,
      data.documentDisplayFileName
    );
  }

  // Define Grid columns
  const defaultColumns: StewartTableColumn<any>[] = [
    {
      field: "documentDisplayFileName",
      name: "DOCUMENTS",
      sortable: true,
      width: isProductStatusVisible ? 500 : 600,
      valueGetter: (row: Document) => {
        return (
          <Box sx={{ color: row.isHidden === 1 ? colors.grey11 : "inherit" }}>
            <TooltipCell
              title={row.documentDisplayFileName || ""}
            ></TooltipCell>
          </Box>
        );
      },
    },
    {
      field: "documentSize",
      name: "SIZE",
      sortable: true,
      // width: 100,
      valueGetter: (row: Document) => {
        return (
          <Box sx={{ color: row.isHidden === 1 ? colors.grey11 : "inherit" }}>
            <TooltipCell
              title={`${formatFileSize(
                row.documentSize,
                FileSizeType.MegaByte,
                true
              )}`}
            ></TooltipCell>
          </Box>
        );
      },
    },
  ];

  const additionalColumns: StewartTableColumn<any>[] = [
    {
      field: "documentCreatedDate",
      name: "UPLOADED DATE/TIME",
      sortable: true,
      // width: 500,
      valueGetter: (row: Document) => {
        return (
          <Box sx={{ color: row.isHidden === 1 ? colors.grey11 : "inherit" }}>
            <TooltipCell title={(formatDateRegEx(row.documentCreatedDate || "") || "")}></TooltipCell>
          </Box>
        );
      },      
    },
    {
      field: "documentCreatedByUserName",
      name: "UPLOADED BY",
      sortable: true,
      valueGetter: (row: Document) => {
        return (
          <Box sx={{ color: row.isHidden === 1 ? colors.grey11 : "inherit" }}>
            <TooltipCell title={row.documentCreatedByUserName || ""}></TooltipCell>
          </Box>
        );
      },          
    },
    {
      field: "originationSource",
      name: "SOURCE",
      sortable: true,
      valueGetter: (row: Document) => {
        return (
          <Box sx={{ color: row.isHidden === 1 ? colors.grey11 : "inherit" }}>
            <TooltipCell title={row.originationSource || ""}></TooltipCell>
          </Box>
        );
      },       
    },
  ];

  const actionColumnWidth = isProductStatusVisible && !hasHideUnHideDocsAccess ? 100  : 150;
  const actionsColumn: StewartTableColumn<any>[] = [
    {
      field: "Actions",
      name: "",
      sortable: false,
      classname: "actions",
      width: actionColumnWidth, // isProductStatusVisible ? 100 : 150,
      valueGetter: (rowData: Document) => {
        const isHidden = rowData.isHidden;

        return (
        <>
          {isTitleSearchGridInEditMode &&
          currentEditingRow?.rowId === rowData?.rowId ? (
            <Box sx={{ marginLeft: "40px" }}>
              <CustomTooltip
                title="Save"
                placement="top"
                aria-label="save"
                arrow
              >
                <IconButton
                  id="saveDocumentButton"
                  className="actionButton"
                  aria-label="Save"
                  disabled={
                    !methods.formState.isValid || !methods.formState.isDirty || disabled 
                  }
                  sx={{
                    color: methods.formState.isValid
                      ? colors.blue01
                      : colors.blue11,
                    "&.Mui-disabled": {
                      color: colors.blue11,
                    },
                  }}
                >
                  <SaveIcon id="fileSaveOnEdit" className="saveDocumentIcon" />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip
                title="Cancel"
                placement="top"
                aria-label="save"
                arrow
              >
                <IconButton
                  className="actionButton"
                  aria-label="Cancel"
                  disabled={disabled}
                  onClick={() => handleCancel(rowData)}
                >
                  <CloseIcon sx={{ color: colors.blue01 }} />
                </IconButton>
              </CustomTooltip>
            </Box>
          ) : (
            <>
              {!isProductStatusVisible && (
                <CustomTooltip
                  title="Edit"
                  placement="top"
                  aria-label="Edit"
                  arrow
                >
                  <IconButton
                    onClick={() => handleEdit(rowData)}
                    className="actionButton"
                    aria-label="Edit"
                    disabled={disabled || isTitleSearchGridInEditMode}
                    sx={{
                      color: colors.blue01,
                      "&.Mui-disabled": {
                        color: colors.blue11,
                      },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </CustomTooltip>
              )}
              {isProductStatusVisible && (
                <CustomTooltip
                  title="Download"
                  placement="top"
                  aria-label="View"
                  arrow
                >
                  <IconButton
                    className="actionButton"
                    aria-label="Download"
                    disabled={
                      !permissionCodes?.includes(
                        PERMISSIONS.POLICYIMAGES_VIEWDOCS
                      )
                        ? true
                        : isTitleSearchGridInEditMode
                    }
                    onClick={() => handleDownload(rowData)}
                    sx={{
                      color: colors.blue01,
                      "&.Mui-disabled": {
                        color: colors.blue11,
                      },
                    }}
                  >
                    <DownloadIcon
                      color={colors.blue01}
                      // isDisabled={
                      //   !permissionCodes?.includes(
                      //     PERMISSIONS.POLICYIMAGES_VIEWDOCS
                      //   )
                      //     ? true
                      //     : isTitleSearchGridInEditMode
                      // }
                    />
                  </IconButton>
                </CustomTooltip>
              )}
              <CustomTooltip
                title="View"
                placement="top"
                aria-label="View"
                arrow
              >
                <IconButton
                  className="actionButton"
                  aria-label="View"
                  disabled={isTitleSearchGridInEditMode}
                  onClick={() => handleView(rowData)}
                  sx={{
                    color: colors.blue01,
                    "&.Mui-disabled": {
                      color: colors.blue11,
                    },
                  }}
                >
                  <ViewIcon isDisabled={isTitleSearchGridInEditMode} />
                </IconButton>
              </CustomTooltip>
              {isProductStatusVisible && hasHideUnHideDocsAccess && (
                <CustomTooltip
                  title={isHidden ? "Unhide Document" : "Hide Document"}
                  placement="top"
                  arrow
                >
                  <IconButton
                    disabled={disabled}
                    onClick={async () => {
                      openAutomaticProgressDialog(
                        UIConstants.SAVE_TITLE_SEARCH_DOCUMENT_IN_PROGRESS_MESSAGE
                      );                      
                      const newIsHidden = isHidden ? 0 : 1;
                      const apiDocument = mapRequiredApiDocument(rowData);
                      apiDocument.IsHidden = newIsHidden;
                      const result =  await saveDocument(apiDocument);
                       
                      if (result.status === ActionResultStatus.Success) {
                        await performSearch!();

                        closeAutomaticProgressDialog();
                        if (newIsHidden === 1) {
                          showToast({
                            type: "warning",
                            title: UPDATE_DOCUMENT_ISHIDDEN_SUCCESS_TITLE,
                            message: UPDATE_DOCUMENT_ISHIDDEN_SUCCESS_MESSAGE,
                          });
                        }
                      } else {
                        closeAutomaticProgressDialog();
                        // show error here
                        const errorMessage = result.error?.response?.data
                          ? result.error.response.data
                          : result.error.response.statusText;

                        setSaveErrorMessage(errorMessage);
                        setOpenSaveErrorDialog(true);
                      }

                    }}
                    className="actionButton"
                    sx={{ color: colors.blue01 }}
                  >
                    {!isHidden ? <ToggleOnIcon /> : <ToggleOffIcon />}
                  </IconButton>
                </CustomTooltip>
              )}

              {!isProductStatusVisible && (
                <CustomTooltip
                  title="Delete"
                  placement="top"
                  aria-label="Delete"
                  arrow
                >
                  <IconButton
                    className="actionButton"
                    aria-label="Delete"
                    onClick={() => handleDelete(rowData)}
                    sx={{
                      color: colors.blue01,
                      "&.Mui-disabled": {
                        color: colors.blue11,
                      },
                    }}
                    disabled={disabled || isTitleSearchGridInEditMode}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CustomTooltip>
              )}
            </>
          )}
        </>
        )
    },
    },
  ];

  const columns = isProductStatusVisible ?  [...defaultColumns, ...additionalColumns, ...actionsColumn] : [...defaultColumns, ...actionsColumn];

  const [tableColumns, setTableColumns] = useState(columns);

  async function save(includedFile:boolean = false, exitAfterSave:boolean = false) {
    if (currentEditingRow) {
      openAutomaticProgressDialog(
        UIConstants.SAVE_TITLE_SEARCH_DOCUMENT_IN_PROGRESS_MESSAGE
      );
      setTitleSearchGridInEditMode(false);

      const fileName = getValues("documentDisplayFileName")?.trim();
      const apiDocument = mapRequiredApiDocument(currentEditingRow);
      apiDocument.DocumentDisplayFileName = `${fileName}.${currentEditingRow?.documentFileTypeCode?.toLowerCase()}`;
      const result = await saveDocument(apiDocument);

      if (result.status === ActionResultStatus.Success) {
        if (includedFile && !isReadOnly) {
          await saveFile();
        }

        if (!exitAfterSave) {
          if (!isReadOnly) {
            await reloadFile();
          } else {
            await reloadFileActivities();
          }
          await performSearch!();
        }
        closeAutomaticProgressDialog();
        showToast({
          type: 'success',
          title: SAVE_DOCUMENT_NAME_SUCCESS_TITLE
        });           
      } else {
        closeAutomaticProgressDialog();
        // show error here
        const errorMessage = result.error?.response?.data
          ? result.error.response.data
          : result.error.response.statusText;

        setSaveErrorMessage(errorMessage);
        setOpenSaveErrorDialog(true);
      }
    }
  }

  async function onSaveImageHandler() {
    setOpenSaveImageDialog(false);
    setTitleSearchGridInEditMode(false);
    await save(true, false);
    navigateAfterSaveChangesDialog(navigateTo.current);
  }

  async function onSaveExitImageHandler() {
    setOpenSaveExitImageDialog(false);
    setTitleSearchGridInEditMode(false);
    await save(true, true);

    navigateAfterSaveChangesDialog(navigateTo.current);

    if (navigateTo.current.url === getUrlByMenuId(menuIds.menuCreateFile)) {
      setTimeout(() => {
        document?.documentElement?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }

  }

  function onSaveImageCancelHandler() {
    setOpenSaveImageDialog(false);
    // setFileEditMode(false);
  }

  function onSaveExitImageCancelHandler() {
    setOpenSaveExitImageDialog(false);
    // setFileEditMode(false);
  }

  function onContinueSaveImageHandler() {
    setOpenSaveImageDialog(false)
    navigateAfterSaveChangesDialog(navigateTo.current);
  }

  function onContinueSaveExitImageHandler() {
    setOpenSaveExitImageDialog(false)
    navigateAfterSaveChangesDialog(navigateTo.current);
  }

  const handleCancel = (rowData: Document) => {
    setTitleSearchGridInEditMode(false);
    rowData.isEdit = false;
    resetField(`documentDisplayFileName`);
  };

  const handleDelete = async (data: Document) => {
    setOpenConfirmationDialog(true);
    setDeleteConfirmationMessage(`
    Delete '${data.documentDisplayFileName}' ?`)
    setIdToDelete(data.rowId);
  };

  const handleYes = async (data: Document) => {
    setOpenConfirmationDialog(false);

    // Delete document
    openAutomaticProgressDialog();
    const result = await deleteDocument(idToDelete || 0);
  
    if (result.status === ActionResultStatus.Success) {
      if (reloadFileActivities) {
        await reloadFileActivities();
      }
      await performSearch!();
      closeAutomaticProgressDialog();
      showToast({
        type: 'success',
        title: DELETE_SUCCESS_TITLE
      });   
    }
    else {
      closeAutomaticProgressDialog();
      // show error here 
      const errorMessage =
        result.error?.response?.data 
          ? result.error.response.data
          : result.error.response.statusText;

      setSaveErrorMessage(errorMessage);
      setOpenSaveErrorDialog(true);
    }
  };

  const onSaveErrorOkHandler = () => {
    setOpenSaveErrorDialog(false);
    performSearch!();
  };

  const onSaveErrorCancelHandler = () => {
    setOpenSaveErrorDialog(false);
    performSearch!();
  };

  const handleOnYesErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const handleClosePdfViewer = () => {
    setOpenPdfViewer(false);
  };

  const handleDialogNo = () => {
    setOpenConfirmationDialog(false);
  };

  const handleDialogCancel = () => {
    setOpenConfirmationDialog(false);
  };


  useEffect(() => {
    const updatedColumns = columns.map((column) => {
      if (column.field === "documentDisplayFileName") {
        return {
          ...column,
          valueGetter: ({ rowId, documentDisplayFileName }: any) =>
            isTitleSearchGridInEditMode && currentEditingRow?.rowId === rowId ? (
              <CustomTextField
                name={`documentDisplayFileName`}
                defaultValue={documentDisplayFileName.split(".").slice(0, -1).join(".")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(`documentDisplayFileName${rowId}`, event.target?.value)
                }
                maxLength={100}
              />
            ) : (
              <TooltipCell title={documentDisplayFileName}></TooltipCell>
            ),
        };
      }
      return column;
    });
    setTableColumns(updatedColumns);
  }, [isTitleSearchGridInEditMode]);

  useEffect(() => {
    const isSubmittted: boolean = (processedStatusTypeCode  &&  processedStatusTypeCode !== TitleSearchProcessedStatusType.SCFileCreated) ? true: false;
    setIsProductStatusVisible(isSubmittted);
  },[processedStatusTypeCode])

  useEffect(() => {
    const columns = isProductStatusVisible ?  [...defaultColumns, ...additionalColumns, ...actionsColumn] : [...defaultColumns, ...actionsColumn];
    setTableColumns(columns);
  }, [isProductStatusVisible])

  useEffect(() => {
    return (() => {
      // setFileShowToast(false);
      setTitleSearchGridInEditMode(false);
    })
  }, [])

  useEffect(() => {
    setDisableSave(!methods.formState.isValid || !methods.formState.isDirty)
  }, [methods.formState])

  // useEffect(() => {
  //   if (hasSentMessageSuccessfully) {
  //      performSearch!()
  //   }
  // }, [hasSentMessageSuccessfully]);

  useEffect(() => {

    const onSaveDialogImageNameEditingHandler = async (event: Event) => {
      const clickedOnFileSaveOnEdit = (document.querySelector("#fileSaveOnEdit") as Element)?.contains(event.target as Node);
      const navigationInfo = getNavigationInfo(event);
  
      // console.log("navigateToInfo:", navigateToInfo);
      if (navigationInfo?.navigateToInfo && navigationInfo.navigateToInfo.navigateToType) {
        navigateTo.current = navigationInfo?.navigateToInfo;
      }

      // -- also add for "Save" button above - will need to add
      if (isTitleSearchGridInEditMode && !disableSave) {
        if (clickedOnFileSaveOnEdit) {
          await save(true, false);
          return;
        }
  
        if (
          navigationInfo?.navigateToMenuHome ||
          navigationInfo?.navigateToMenuFiles ||
          navigationInfo?.navigateToMenuCreateFile ||
          navigationInfo?.navigateToMenuPaymentsReportPay ||
          navigationInfo?.navigateToMenuPaymentsPendingPayments ||
          navigationInfo?.navigateToMenuPolicyImages ||
          navigationInfo?.navigateToMenuStewartResources ||
          navigationInfo?.navigateToMenuProfileSettings ||
          navigationInfo?.navigateToMenuManageSignatures ||
          navigationInfo?.navigateToMenuHelpCenter ||
          navigationInfo?.navigateToMenuLogout 
        ) {
          event.preventDefault();
          setOpenSaveExitImageDialog(true);
          return;
        }
  
        if (
          navigationInfo?.navigateToGlobalAccessAgency ||
          navigationInfo?.clickedOnChangeGlobalAccessAgency ||
          navigationInfo?.clickedOnRemoveGlobalAccessAgency ||
          navigationInfo?.navigateToCplTab ||
          navigationInfo?.navigateToAalTab ||
          navigationInfo?.navigateToJacketTab ||
          navigationInfo?.navigateToSaendoTab ||
          navigationInfo.navigateToPolicyUploadTab 
        ) {
          event.preventDefault();
          setOpenSaveImageDialog(true);
          return;
        }
      }
      // Notes: clicking on Policy Upload tab -- will stay on same the tab without prompting any save changes question
    };

    if (isTitleSearchGridInEditMode && disableSave === false) {
      document.addEventListener("mousedown", onSaveDialogImageNameEditingHandler, true);
    }
    else {
      document.removeEventListener("mousedown", onSaveDialogImageNameEditingHandler, true);
    }
    
    return () => {
      document.removeEventListener("mousedown", onSaveDialogImageNameEditingHandler, true);
    };
  }, [isTitleSearchGridInEditMode, disableSave]);

  return (
    <>
      <FormProvider {...methods}>
        <StyledTableContainer>
          <StewartTable
            cols={tableColumns}
            rows={rows}
            page={page}
            rowsPerPage={999999}
            showActionRow={false} // false
            onColumnResize={() => {}}
            noRowsMessage={noRowsMessage}
            orderBy={orderBy}
            order={order}
            useDbPagingSorting={true}
            onRequestSort={onRequestSort}
            selectionConfig={selectionConfig}
          />
        </StyledTableContainer>
      </FormProvider>
      <ConfirmationDialogV2
        isOpen={openSaveImageDialog}
        mainMessage={MAIN_MESSAGE}
        additionalMessage={ADDITIONAL_MESSAGE}
        singleActionButtonText="Save" //"Save"
        onYes={onSaveImageHandler} //{onSaveHandler}
        onNo={onContinueSaveImageHandler}
        buttonNoText={"Continue"}
        onCancel={onSaveImageCancelHandler}
      />
      <ConfirmationDialogV2
        isOpen={openSaveExitImageDialog}
        mainMessage={MAIN_MESSAGE}
        additionalMessage={ADDITIONAL_MESSAGE2}
        singleActionButtonText="Save & Exit"
        onYes={onSaveExitImageHandler}
        onNo={onContinueSaveExitImageHandler}
        buttonNoText={"Continue"}
        onCancel={onSaveExitImageCancelHandler}
      />
      <ConfirmationDialogV2
        isOpen={openSaveErrorDialog}
        mainMessage={saveErrorMessage}
        singleActionButton={true}
        singleActionButtonText="OK"
        onYes={onSaveErrorOkHandler}
        onCancel={onSaveErrorCancelHandler}
      />
      <ConfirmationDialogV3
        confirmationMessage={deleteConfirmationMessage}
        isOpen={openConfirmationDialog}
        cancelActionButton={true}
        onYes={handleYes}
        onNo={handleDialogNo}
        onCancel={handleDialogCancel}
        singleActionButtonText={"Delete"}
        buttonNoText={"Cancel"}
      />
      <ErrorDialog
        isOpen={openErrorDialog}
        confirmationMessage={errorMessage}
        correlationId={correlationId}
        onYes={handleOnYesErrorDialog}
      />
      <PdfViewer
        isOpen={openPdfViewer}
        onClose={handleClosePdfViewer}
        pdfDocuments={pdfDocuments}
      />
    </>
  );
}

export default UploadedDocumentsGrid;

